import { twMerge } from '@/lib/classname/twMerge'
import { Divider } from '@/ui/layouts/divider/Divider'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import { type VariantProps, cva } from 'class-variance-authority'
import { TextSkeleton } from '../skeleton/TextSkeleton'
import { tabUnderlineThickness, tabsBaselineDividerClassName } from './Tabs.client'

const tabsSkeletonItemCva = cva('flex items-center gap-1 border-solid', {
  variants: {
    variant: {
      md: `relative z-[1] ${tabUnderlineThickness} border-brand-2-200 py-2`,
      sm: '',
    },
  },
})

type TabsSkeletonVariantProps = Required<VariantProps<typeof tabsSkeletonItemCva>>

export type TabsSkeletonProps = UiComponentProps & {
  /** Visual variant matching the Tabs component */
  variant: TabsSkeletonVariantProps['variant']

  /** Number of tab placeholders to display */
  count?: number
}

export function TabsSkeleton({
  variant,
  count = 4,
  className,
  id,
  'data-testid': dataTestId,
}: TabsSkeletonProps) {
  return (
    <div
      id={id}
      className={twMerge('flex w-full flex-col', className)}
      data-testid={dataTestId ?? 'tabs-skeleton'}
      aria-hidden
    >
      <div className="relative flex w-full gap-4">
        {Array.from({ length: count }, (_, index) => (
          <div key={index} className={tabsSkeletonItemCva({ variant })}>
            {variant === 'md' && <BaseSkeleton className="size-[0.875rem] rounded-full" />}
            <TextSkeleton size="md" leading="light" className="w-15" />
          </div>
        ))}
        {variant === 'md' && (
          <Divider
            direction="horizontal"
            variant="line"
            aria-hidden
            className={tabsBaselineDividerClassName}
          />
        )}
      </div>
    </div>
  )
}
